import type { AxiosInstance } from 'axios';
import type { ClientState } from '../types/state';

/**
 * Dependencies for retry queue function.
 * Passed as parameter to avoid circular dependencies.
 */
export interface RetryQueueDependencies {
  /** Client state containing queues */
  state: ClientState;

  /** Axios instance for retrying failed requests */
  authenticatedInstance: AxiosInstance;

  /** Function to handle token refresh */
  handleTokenRefresh: () => Promise<string>;
}

/**
 * Retry all queued and failed requests.
 *
 * This function is exposed via the onNetworkError callback and allows
 * users to manually retry requests after a connection issue.
 *
 * Flow:
 * 1. Refresh the access token (to avoid using stale/expired token)
 * 2. Token refresh automatically processes the regular request queue
 * 3. Then retry all requests that failed due to cold start/network errors
 *
 * This is called when the user clicks "Retry" in the connection error dialog.
 *
 * @param deps Dependencies (state, instances, handlers)
 * @returns Promise that resolves when all retries complete
 * @throws Error if token refresh or retries fail
 *
 * @example
 * ```typescript
 * // In connection error dialog
 * const handleRetry = async () => {
 *   try {
 *     await retryQueuedRequests(deps);
 *     // All requests retried successfully
 *   } catch (error) {
 *     // Show error to user
 *   }
 * };
 * ```
 */
export async function retryQueuedRequests(
  deps: RetryQueueDependencies
): Promise<void> {
  const { state, authenticatedInstance, handleTokenRefresh } = deps;

  console.log('🔄 Manual retry triggered by user');

  try {
    // Refresh token first - this will process the token refresh queue on success
    await handleTokenRefresh();

    // Also retry all failed requests from cold start/network errors
    if (state.failedRequestsQueue.length > 0) {
      console.log(
        `🔄 Retrying ${state.failedRequestsQueue.length} failed request(s) from cold start`
      );

      const failedRequests = [...state.failedRequestsQueue];
      state.failedRequestsQueue = []; // Clear queue before retrying

      for (const { resolve, reject, config } of failedRequests) {
        try {
          const response = await authenticatedInstance(config);
          console.log(`✅ Retry succeeded for ${config.url}`);
          resolve(response);
        } catch (error) {
          console.error(`❌ Retry failed for ${config.url}:`, error);
          reject(error);
        }
      }

      // Reset cold start dialog flag after successful manual retry
      if (state.coldStartDialogShown) {
        state.coldStartDialogShown = false;
        state.coldStartDialogShownAt = null;
        console.log('✅ Manual retry completed - dialog flag reset');
      }
    }
  } catch (error) {
    // Token refresh or retry failed, propagate error to UI
    throw error;
  }
}
